import React, { useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import AppleRoundedIcon from '@mui/icons-material/Apple';
import GoogleRoundedIcon from '@mui/icons-material/Google';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import FormControlLabel from '@mui/material/FormControlLabel';

import type { FinixTippingComponentConfig, FlowComponentRenderProps, FlowValues } from '../types';
import { useFlowRuntime } from '../FlowRuntimeContext';
import ApruvTermsNotice from '../../components/common/ApruvTermsNotice';
import { submitTipPayment, type FinixPaymentType } from '../../services/paymentService';
import {
  MIN_TIP_AMOUNT,
  formatCurrency,
  getFinixBreakdown,
  parseTipAmount,
} from './finixTipping/finixTipFee';
import FinixPaymentMethodOption, {
  type FinixPaymentMethodConfig,
} from './finixTipping/FinixPaymentMethodOption';
import type { FinixPaymentMethod, FinixTippingValue } from './finixTipping/types';

const paymentMethods: FinixPaymentMethodConfig[] = [
  {
    description: 'Secure hosted card token submitted through the legacy Finix endpoint.',
    icon: CreditCardRoundedIcon,
    id: 'card',
    label: 'Credit or debit card',
    mark: 'CARD',
  },
  {
    description: 'Google Pay token submitted through the legacy Finix endpoint.',
    icon: GoogleRoundedIcon,
    id: 'google_pay',
    label: 'Google Pay',
    mark: 'G',
  },
  {
    description: 'Apple Pay token submitted through the legacy Finix endpoint.',
    icon: AppleRoundedIcon,
    id: 'apple_pay',
    label: 'Apple Pay',
    mark: 'APPLE',
  },
];

const paymentTypeByMethod: Record<FinixPaymentMethod, FinixPaymentType> = {
  apple_pay: 'APPLE_PAY',
  card: 'CARD',
  google_pay: 'GOOGLE_PAY',
};

function randomIdempotencyKey() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `cx-tip-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function recordValue(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;
}

function stringValue(value: unknown) {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return undefined;
}

function numberOrStringValue(value: unknown) {
  if (typeof value === 'number' || typeof value === 'string') return value;
  return undefined;
}

function findAppointmentDetails(values: FlowValues) {
  return (
    recordValue(values.appointmentDetails) ??
    recordValue(values.appointment_details) ??
    recordValue(values.appointment)
  );
}

function resolveFinixContext(
  config: FinixTippingComponentConfig,
  values: FlowValues,
  paymentMethod: FinixPaymentMethod
) {
  const appointmentDetails = findAppointmentDetails(values);
  const appointment = recordValue(appointmentDetails?.appointment_details) ?? appointmentDetails;
  const technician = recordValue(appointmentDetails?.technician_details);

  return {
    appointmentId:
      config.appointmentId ??
      numberOrStringValue(appointment?.AppointmentId) ??
      numberOrStringValue(values.appointmentId) ??
      0,
    companyName:
      config.companyName ??
      stringValue(appointment?.CompanyName) ??
      stringValue(values.companyName) ??
      'Apruv',
    merchantId:
      config.merchantId ??
      stringValue(technician?.finix_merchant) ??
      stringValue(values.finixMerchantId),
    sessionKey:
      config.sessionKey ?? stringValue(values.finixSessionKey) ?? `cx-session-${config.id}`,
    token:
      config.paymentToken ??
      stringValue(values.finixPaymentToken) ??
      `cx-${paymentMethod}-token-${config.id}`,
  };
}

const FinixTippingBlock: React.FC<FlowComponentRenderProps<FinixTippingComponentConfig>> = ({
  config,
  goNext,
  onAction,
  onInteract,
  setValue,
  value,
  values,
}) => {
  const { mode: runtimeMode } = useFlowRuntime();
  const existingValue = (value ?? {}) as FinixTippingValue;
  const [selectedAmount, setSelectedAmount] = useState<number | undefined>(existingValue.amount);
  const [customAmount, setCustomAmount] = useState(existingValue.customAmount ?? '');
  const [coverProcessingFee, setCoverProcessingFee] = useState(
    existingValue.coverProcessingFee ?? true
  );
  const [paymentMethod, setPaymentMethod] = useState<FinixPaymentMethod>(
    existingValue.paymentMethod ?? 'card'
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(randomIdempotencyKey);

  const customTipAmount = customAmount.trim() ? parseTipAmount(customAmount) : undefined;
  const activeAmount = customAmount.trim() ? customTipAmount : selectedAmount;
  const hasInvalidCustomAmount = !!customAmount.trim() && !customTipAmount;
  const breakdown = getFinixBreakdown(activeAmount ?? 0, coverProcessingFee);
  const termsEnabled = config.termsEnabled !== false;
  const finixContext = useMemo(
    () => resolveFinixContext(config, values, paymentMethod),
    [config, paymentMethod, values]
  );

  const updateValue = (nextValue: Partial<FinixTippingValue>) => {
    const nextAmount = nextValue.amount ?? activeAmount ?? 0;
    const nextCoverFee = nextValue.coverProcessingFee ?? coverProcessingFee;

    setValue(config.id, {
      ...existingValue,
      ...nextValue,
      ...getFinixBreakdown(nextAmount, nextCoverFee),
      paymentGateway: 'finix',
    });
  };

  const handleSelectAmount = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount('');
    setSubmitError(null);
    updateValue({ amount, customAmount: '', status: 'amount_selected' });
  };

  const handleCustomAmount = (nextCustomAmount: string) => {
    setCustomAmount(nextCustomAmount);
    setSelectedAmount(undefined);
    setSubmitError(null);
    updateValue({
      amount: parseTipAmount(nextCustomAmount) ?? 0,
      customAmount: nextCustomAmount,
      status: 'amount_selected',
    });
  };

  const skipTip = () => {
    const skippedValue = {
      amount: 0,
      paymentGateway: 'finix' as const,
      status: 'skipped' as const,
    };

    setValue(config.id, skippedValue);
    const interaction = {
      componentId: config.id,
      label: config.noThanksLabel ?? 'No thanks',
      metadata: { values: { [config.id]: skippedValue } },
      type: (onInteract ? 'continue' : 'tip') as 'continue' | 'tip',
      value: 'skipped',
    };
    if (onInteract) onInteract(interaction);
    else {
      onAction?.(interaction);
      goNext();
    }
  };

  const submitTip = async () => {
    if (!activeAmount || submitting) return;

    setSubmitting(true);
    setSubmitError(null);

    const finalValue = {
      ...breakdown,
      coverProcessingFee,
      customAmount,
      paymentGateway: 'finix' as const,
      paymentMethod,
      requestId: idempotencyKey,
      status: 'submitted' as const,
    };

    try {
      if (runtimeMode !== 'preview') {
        await submitTipPayment({
          appointmentId: finixContext.appointmentId,
          companyName: finixContext.companyName,
          idempotencyKey,
          merchantId: finixContext.merchantId,
          sessionKey: finixContext.sessionKey,
          tip: Math.round(breakdown.total * 100),
          token: finixContext.token,
          type: paymentTypeByMethod[paymentMethod],
        });
      }

      setValue(config.id, finalValue);
      const interaction = {
        componentId: config.id,
        label: config.sendTipLabel ?? 'Send tip',
        metadata: {
          paymentGateway: 'finix',
          paymentMethod,
          values: { [config.id]: finalValue },
        },
        type: 'tip' as const,
        value: activeAmount,
      };
      if (onInteract) onInteract(interaction);
      else {
        onAction?.(interaction);
        goNext();
      }
    } catch {
      setIdempotencyKey(randomIdempotencyKey());
      setSubmitError('Payment failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card
      sx={{
        bgcolor: 'var(--cx-brand-surface)',
        border: '1px solid',
        borderColor: 'var(--cx-brand-border)',
        boxShadow: 'none',
        color: 'var(--cx-brand-text)',
        p: 2,
      }}
    >
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
            {config.tipAmountLabel ?? "Tip to make your technician's day"}
          </Typography>
          {config.subtitle ? (
            <Typography variant="body2" sx={{ color: 'var(--cx-brand-text-muted)', mt: 0.5 }}>
              {config.subtitle}
            </Typography>
          ) : null}
        </Box>

        <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
          {config.amounts.map((tipAmount) => {
            const selected = selectedAmount === tipAmount.value && !customAmount.trim();

            return (
              <Button
                aria-pressed={selected}
                key={tipAmount.value}
                onClick={() => handleSelectAmount(tipAmount.value)}
                sx={{
                  bgcolor: selected
                    ? 'var(--cx-brand-button-selected-bg)'
                    : 'var(--cx-brand-button-bg)',
                  border: '1px solid',
                  borderColor: selected
                    ? 'var(--cx-brand-button-selected-border)'
                    : 'var(--cx-brand-button-border)',
                  color: selected
                    ? 'var(--cx-brand-button-selected-text)'
                    : 'var(--cx-brand-button-text)',
                  fontSize: 18,
                  fontWeight: 900,
                  minHeight: 56,
                }}
                type="button"
                variant="outlined"
              >
                {tipAmount.label}
              </Button>
            );
          })}
        </Box>

        {config.allowCustomAmount !== false ? (
          <TextField
            fullWidth
            error={hasInvalidCustomAmount}
            helperText={hasInvalidCustomAmount ? 'Minimum is $1.00' : undefined}
            label="Enter other amount"
            onChange={(event) => handleCustomAmount(event.target.value)}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              },
              htmlInput: {
                inputMode: 'decimal',
                min: MIN_TIP_AMOUNT,
                step: '0.01',
              },
            }}
            type="number"
            value={customAmount}
          />
        ) : null}

        <Box
          sx={{
            bgcolor: 'var(--cx-brand-bg)',
            border: '1px solid',
            borderColor: 'var(--cx-brand-border)',
            borderRadius: 2,
            p: 1.5,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {config.summaryTitle ?? 'Summary'}
          </Typography>
          {[
            ['Tip', formatCurrency(breakdown.amount)],
            ['Payment & payout fees', formatCurrency(breakdown.processingFee)],
            ['Total', formatCurrency(breakdown.total)],
          ].map(([label, amount]) => (
            <Box
              key={label}
              sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.45 }}
            >
              <Typography variant="body2" sx={{ color: 'var(--cx-brand-text-muted)' }}>
                {label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 900 }}>
                {amount}
              </Typography>
            </Box>
          ))}

          <FormControlLabel
            control={
              <Checkbox
                checked={coverProcessingFee}
                onChange={(event) => {
                  setCoverProcessingFee(event.target.checked);
                  updateValue({
                    amount: activeAmount ?? 0,
                    coverProcessingFee: event.target.checked,
                    paymentMethod,
                    status: 'amount_selected',
                  });
                }}
                sx={{
                  color: 'var(--cx-brand-text-muted)',
                  '&.Mui-checked': { color: 'var(--cx-brand-primary)' },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: 'var(--cx-brand-text)', fontWeight: 700 }}>
                Cover payment & payout fees {formatCurrency(breakdown.processingFee)}
              </Typography>
            }
            sx={{ mt: 1, mx: 0 }}
          />
        </Box>

        <Stack spacing={1}>
          <Typography variant="subtitle2">{config.paymentTitle ?? 'Payment method'}</Typography>
          {paymentMethods.map((method) => (
            <FinixPaymentMethodOption
              key={method.id}
              method={method}
              onSelect={(nextPaymentMethod) => {
                setPaymentMethod(nextPaymentMethod);
                updateValue({
                  amount: activeAmount ?? 0,
                  coverProcessingFee,
                  paymentMethod: nextPaymentMethod,
                  status: 'amount_selected',
                });
              }}
              selected={paymentMethod === method.id}
            />
          ))}
        </Stack>

        {paymentMethod === 'card' ? (
          <Box
            sx={{
              bgcolor: 'var(--cx-brand-bg)',
              border: '1px dashed',
              borderColor: 'var(--cx-brand-border)',
              borderRadius: 2,
              p: 1.5,
            }}
          >
            <Typography variant="subtitle2">
              {config.cardFormLabel ?? 'Or pay with card'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'var(--cx-brand-text-muted)' }}>
              Finix hosted card fields provide the token submitted to frontend-api-v1.
            </Typography>
          </Box>
        ) : null}

        {termsEnabled ? (
          <ApruvTermsNotice
            linkLabel={config.termsLinkLabel}
            modalTitle={config.termsModalTitle}
            termsHtml={config.termsHtml}
            text={config.termsText ?? 'By confirming you agree with our'}
          />
        ) : null}

        {submitError ? <Alert severity="error">{submitError}</Alert> : null}

        <Button
          disabled={!activeAmount || submitting}
          fullWidth
          onClick={submitTip}
          sx={{
            bgcolor: 'var(--cx-brand-button-selected-bg)',
            border: '1px solid var(--cx-brand-button-selected-border)',
            color: 'var(--cx-brand-button-selected-text)',
            fontWeight: 900,
            minHeight: 54,
            '&:disabled': {
              bgcolor: 'var(--cx-brand-button-hover-bg)',
              borderColor: 'var(--cx-brand-button-border)',
              color: 'var(--cx-brand-text-muted)',
            },
            '&:hover': { bgcolor: 'var(--cx-brand-button-hover-bg)' },
          }}
          type="button"
          variant="contained"
        >
          {submitting ? 'Processing...' : (config.sendTipLabel ?? 'Send tip')}
        </Button>

        <Button
          disabled={submitting}
          fullWidth
          onClick={skipTip}
          sx={{
            bgcolor: 'var(--cx-brand-button-bg)',
            border: '1px solid var(--cx-brand-button-border)',
            color: 'var(--cx-brand-button-text)',
            fontWeight: 900,
            minHeight: 54,
          }}
          type="button"
          variant="outlined"
        >
          {config.noThanksLabel ?? 'No thanks'}
        </Button>
      </Stack>
    </Card>
  );
};

export default FinixTippingBlock;
